/* @flow */

import React, { Suspense, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useToaster from 'rsuite/useToaster';
import Modal from 'rsuite/Modal';
import Button from 'rsuite/Button';
import Carousel from 'rsuite/Carousel';
import Notification from 'rsuite/Notification';
import { useNavigate } from 'react-router-dom';

import 'react-alice-carousel/lib/alice-carousel.css';

import { getHome } from '../actions/home';
import { getOffers } from '../actions/offers';
import { getCurrencyTypeDescription } from '../utils/currencyTypes';
import { isVip } from '../utils/user';
import { validatePointsForProduct } from '../utils/cartPoints';
import { toggleProductFromFavorite } from '../actions/products';
import { addProductToCart } from '../actions/account';

import Footer from '../components/Footer';

import './Home.css';

const AliceCarousel = React.lazy(() => import('react-alice-carousel'));

const FIREBASE_STORAGE_BASE_URL =
  'https://firebasestorage.googleapis.com/v0/b/appthapi-82dae.appspot.com/o';

function buildStorageMediaUrl(storagePath) {
  return `${FIREBASE_STORAGE_BASE_URL}/${encodeURIComponent(storagePath)}?alt=media`;
}

function getHomeImageUrl(imageId) {
  return buildStorageMediaUrl(`${window.configClientKey}/home/${imageId}`);
}

function getHomeCoverVariantUrl(imageId, variant) {
  return buildStorageMediaUrl(
    `${window.configClientKey}/home/optimized/${imageId}-${variant}.webp`,
  );
}

function getProductThumbnailUrl(imageId) {
  return buildStorageMediaUrl(
    `${window.configClientKey}/products/thumb/${imageId}`,
  );
}

function scheduleIdleTask(callback) {
  if (typeof window === 'undefined') {
    callback();
    return () => {};
  }

  if (window.requestIdleCallback) {
    const idleId = window.requestIdleCallback(callback, { timeout: 2000 });
    return () => window.cancelIdleCallback(idleId);
  }

  const timeoutId = window.setTimeout(callback, 250);
  return () => window.clearTimeout(timeoutId);
}

function Home() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toaster = useToaster();
  const home = useSelector((state) => state.client.home);
  const productsInOffer = useSelector((state) => state.products.onOffer);
  const products = useSelector((state) => state.products);
  const cart = useSelector((state) => state.account.cart);
  const currentUser = useSelector((state) => state.account.user) || {};
  const homeProducts = home.products || {};
  const manualOffers = home.offers || {};
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedManualOffer, setSelectedManualOffer] = useState(null);
  const [selectedCoverImageId, setSelectedCoverImageId] = useState(null);
  const [useOriginalCoverImage, setUseOriginalCoverImage] = useState(false);
  const [showOfferSections, setShowOfferSections] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );

  useEffect(() => {
    const cancelIdleTask = scheduleIdleTask(() => {
      dispatch(getOffers());
      setShowOfferSections(true);
    });

    return cancelIdleTask;
  }, [dispatch]);

  useEffect(() => {
    if (Object.keys(home).length === 0) {
      dispatch(getHome());
    }
  }, [dispatch, home]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleViewportChange = (event) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleViewportChange);
      return () =>
        mediaQuery.removeEventListener('change', handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  useEffect(() => {
    const coverCarousel = home.carousel || [];

    if (!coverCarousel.length) {
      setSelectedCoverImageId(null);
      return;
    }

    setSelectedCoverImageId((currentCoverImageId) => {
      if (currentCoverImageId && coverCarousel.includes(currentCoverImageId)) {
        return currentCoverImageId;
      }

      return coverCarousel[Math.floor(Math.random() * coverCarousel.length)];
    });
  }, [home.carousel]);

  useEffect(() => {
    setUseOriginalCoverImage(false);
  }, [selectedCoverImageId, isMobileViewport]);

  const preferredCoverVariant = isMobileViewport ? 'mobile' : 'desktop';
  const optimizedCoverImageUrl = selectedCoverImageId
    ? getHomeCoverVariantUrl(selectedCoverImageId, preferredCoverVariant)
    : '';
  const currentCoverImageUrl = selectedCoverImageId
    ? useOriginalCoverImage
      ? getHomeImageUrl(selectedCoverImageId)
      : optimizedCoverImageUrl
    : '';

  useEffect(() => {
    if (
      typeof document === 'undefined' ||
      !selectedCoverImageId ||
      useOriginalCoverImage
    ) {
      return undefined;
    }

    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'image';
    preloadLink.href = optimizedCoverImageUrl;
    document.head.appendChild(preloadLink);

    return () => {
      if (preloadLink.parentNode) {
        preloadLink.parentNode.removeChild(preloadLink);
      }
    };
  }, [optimizedCoverImageUrl, selectedCoverImageId, useOriginalCoverImage]);

  useEffect(() => {
    if (window.configClientKey !== 'nea' || typeof document === 'undefined') {
      return undefined;
    }

    if (document.querySelector('script[data-tiktok-embed="true"]')) {
      return undefined;
    }

    const cancelIdleTask = scheduleIdleTask(() => {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.tiktok.com/embed.js';
      script.dataset.tiktokEmbed = 'true';
      document.body.appendChild(script);
    });

    return cancelIdleTask;
  }, []);

  if (Object.keys(home).length === 0) {
    return <div>Cargando...</div>;
  }

  const openProductModal = (product) => {
    setSelectedProduct(product);
  };

  const closeProductModal = () => {
    setSelectedProduct(null);
  };

  const onClickProduct = (productId) => {
    navigate(`/product/${productId}`);
  };

  const addProductClick = (product, variant, e) => {
    e.stopPropagation();

    // Validar puntos antes de agregar
    const validation = validatePointsForProduct(
      currentUser,
      product.data,
      cart,
      { list: products.list },
      variant,
      1,
    );

    if (!validation.isValid) {
      toaster.push(
        <Notification type="error" header="Puntos Insuficientes" closable>
          <p>{validation.message}</p>
        </Notification>,
        { placement: 'topEnd', duration: 4000 },
      );
      return;
    }

    if (variant) {
      dispatch(addProductToCart(product.id, variant, 1));
    } else {
      dispatch(addProductToCart(product.id, null, 1));
    }
  };

  const toggleFavoriteClick = (product, e) => {
    e.stopPropagation();
    const isFavorite = products.favorites.includes(product.id);
    dispatch(toggleProductFromFavorite(product, isFavorite));
  };

  const isProductInCart = (productId) => {
    return !!cart[productId];
  };

  const calculateBestOffer = (product) => {
    const offers = product.data.offers;
    const cost = parseFloat(product.data.cost);
    let bestOffer = null;
    let lowestPrice = cost;

    if (offers) {
      Object.keys(offers).forEach((key) => {
        const offer = offers[key];
        const offerValue = parseFloat(offer.offerValue);
        let finalPrice = cost;

        const now = new Date();
        const startDate = new Date(offer.startDate.seconds * 1000);
        const endDate = new Date(offer.endDate.seconds * 1000);

        if (now >= startDate && now <= endDate) {
          const isVIP = isVip(currentUser);

          if (offer.applicability === 'vip' && !isVIP) {
            return;
          }

          if (offer.offerType === 'amount') {
            finalPrice = cost - offerValue;
          } else if (offer.offerType === 'percentage') {
            finalPrice = cost - cost * (offerValue / 100);
          } else if (offer.offerType === 'productQuantity') {
            finalPrice = cost;
          }

          if (finalPrice <= lowestPrice) {
            lowestPrice = finalPrice;
            bestOffer = offer;
          }
        }
      });
    }

    return { bestOffer, lowestPrice };
  };

  const filteredProductsInOffer = productsInOffer.filter((product) => {
    const { bestOffer } = calculateBestOffer(product);
    return (
      bestOffer && (bestOffer.applicability !== 'vip' || isVip(currentUser))
    );
  });

  const renderOfferPrices = (product, bestOffer, lowestPrice) => {
    const hasOffer =
      product.data.offers && Object.keys(product.data.offers).length > 0;

    if (
      hasOffer &&
      bestOffer &&
      product.data.variables &&
      product.data.variables.length > 0
    ) {
      // Render a carousel for each variant's price
      return (
        <Carousel
          className="previews-slider"
          shape="bar"
          autoplay
          interval={2000}
        >
          {product.data.variables.map((variant, index) => {
            const variantCost = parseFloat(variant.cost);
            const variantBestOffer = calculateBestOffer({
              ...product,
              data: { ...product.data, cost: variantCost },
            }).bestOffer;
            const variantLowestPrice = calculateBestOffer({
              ...product,
              data: { ...product.data, cost: variantCost },
            }).lowestPrice;

            return (
              <div
                key={index}
                style={{ background: 'white', textAlign: 'center' }}
              >
                <span>{variant.name} : </span>
                <span
                  style={{
                    textDecoration: 'line-through',
                    color: 'red',
                    marginRight: '10px',
                  }}
                >
                  {`${variant.cost} ${getCurrencyTypeDescription(product.data.currency)}`}
                </span>
                {variantBestOffer.offerType !== 'productQuantity' ? (
                  <span>
                    {`${variantLowestPrice.toFixed(2)} ${getCurrencyTypeDescription(product.data.currency)}`}
                  </span>
                ) : (
                  <span>
                    {`${variantBestOffer.offerValue} % menos en el ${variantBestOffer.productQuantity} productos`}
                  </span>
                )}
              </div>
            );
          })}
        </Carousel>
      );
    } else if (hasOffer && bestOffer) {
      // Render single offer price for the product
      return (
        <>
          <span
            style={{
              textDecoration: 'line-through',
              color: 'red',
              marginRight: '10px',
            }}
          >
            {`Antes: ${product.data.cost} ${getCurrencyTypeDescription(product.data.currency)}`}
          </span>
          {bestOffer.offerType !== 'productQuantity' ? (
            <span>
              {`Ahora: ${lowestPrice.toFixed(2)} ${getCurrencyTypeDescription(product.data.currency)}`}
            </span>
          ) : (
            <span>
              {`${bestOffer.offerValue} % menos en el ${bestOffer.productQuantity} productos`}
            </span>
          )}
        </>
      );
    } else {
      // Render regular price if no offer
      return (
        <span>{`Precio: ${product.data.cost} ${getCurrencyTypeDescription(product.data.currency)}`}</span>
      );
    }
  };
  const carouselOptions = {
    autoPlay: true,
    autoPlayInterval: 3000,
    mouseTracking: true,
    disableDotsControls: true,
    disableButtonsControls: false,
    responsive: {
      0: { items: 1 },
      700: { items: 2 },
      1024: { items: 5, itemsFit: 'contain' },
    },
  };

  return (
    <div className="home">
      {selectedCoverImageId ? (
        <div className="cover-carousel">
          <img
            className="cover-image"
            src={currentCoverImageUrl}
            alt="Portada principal"
            width="1600"
            height="800"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            onError={() => {
              if (!useOriginalCoverImage) {
                setUseOriginalCoverImage(true);
              }
            }}
          />
        </div>
      ) : null}

      <h2 style={{ textAlign: 'center', margin: '20px 0' }}>Novedades</h2>

      <Suspense fallback={<div className="home-carousel-skeleton" />}>
        <AliceCarousel
          {...{ ...carouselOptions, infinite: true }}
          className="alice-carousel"
        >
          {Object.values(homeProducts).map((product) => (
            <div
              key={`homep-${product.id}`}
              className="product-card"
              onClick={() => openProductModal(product)}
              style={{ cursor: 'pointer', margin: '20px' }}
            >
              <div className="product-image">
                <img
                  key={`imgc-${product.id}`}
                  src={getHomeImageUrl(product.image)}
                  alt={`Product ${product.image}`}
                  style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="product-details">
                <h4>{product.title}</h4>
              </div>
            </div>
          ))}
        </AliceCarousel>
      </Suspense>

      {/* Ofertas manuales */}
      {Object.keys(manualOffers).length > 0 && (
        <>
          <h2 style={{ textAlign: 'center', margin: '20px 0' }}>Ofertas </h2>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '20px',
            }}
          >
            {Object.values(manualOffers).map((offer) => (
              <div
                key={`manual-offer-${offer.id}`}
                className="product-card"
                style={{
                  width: 300,
                  margin: 10,
                  border: '1px solid #eee',
                  borderRadius: 8,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                  cursor: 'pointer',
                }}
                onClick={() => setSelectedManualOffer(offer)}
              >
                <div className="product-image">
                  <img
                    src={getHomeImageUrl(offer.image)}
                    alt={offer.title}
                    style={{
                      width: '100%',
                      height: '150px',
                      objectFit: 'cover',
                      borderRadius: '8px 8px 0 0',
                    }}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="product-details" style={{ padding: 16 }}>
                  <h4 style={{ marginBottom: 8 }}>{offer.title}</h4>
                  <div
                    style={{
                      marginBottom: 8,
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minHeight: 40,
                    }}
                    dangerouslySetInnerHTML={{ __html: offer.description }}
                  />
                  <Button
                    size="xs"
                    appearance="link"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedManualOffer(offer);
                    }}
                  >
                    Ver más
                  </Button>
                  <div style={{ marginBottom: 8 }}>
                    <span
                      style={{
                        textDecoration: 'line-through',
                        color: 'red',
                        marginRight: 8,
                      }}
                    >
                      {offer.before ? `Antes: ${offer.before}` : ''}
                    </span>
                    <span style={{ color: 'green', fontWeight: 'bold' }}>
                      {offer.after ? `Ahora: ${offer.after}` : ''}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: '#888' }}>
                    {offer.date || ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Modal para ver oferta manual completa */}
          <Modal
            open={!!selectedManualOffer}
            onClose={() => setSelectedManualOffer(null)}
          >
            <Modal.Header>
              <Modal.Title>{selectedManualOffer?.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <img
                src={
                  selectedManualOffer
                    ? getHomeImageUrl(selectedManualOffer.image)
                    : ''
                }
                alt={selectedManualOffer?.title}
                style={{
                  width: '100%',
                  maxHeight: 200,
                  objectFit: 'cover',
                  borderRadius: 8,
                  marginBottom: 16,
                }}
                decoding="async"
              />
              <div
                style={{ marginBottom: 16 }}
                dangerouslySetInnerHTML={{
                  __html: selectedManualOffer?.description || '',
                }}
              />
              <div style={{ marginBottom: 8 }}>
                <span
                  style={{
                    textDecoration: 'line-through',
                    color: 'red',
                    marginRight: 8,
                  }}
                >
                  {selectedManualOffer?.before
                    ? `Antes: ${selectedManualOffer.before}`
                    : ''}
                </span>
                <span style={{ color: 'green', fontWeight: 'bold' }}>
                  {selectedManualOffer?.after
                    ? `Ahora: ${selectedManualOffer.after}`
                    : ''}
                </span>
              </div>
              <div style={{ fontSize: 13, color: '#888' }}>
                {selectedManualOffer?.date || ''}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                onClick={() => setSelectedManualOffer(null)}
                appearance="subtle"
              >
                Cerrar
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}

      {window.configClientKey === 'nea' && (
        <blockquote
          className="tiktok-embed"
          cite="https://www.tiktok.com/@nea_bolivia"
          data-unique-id="nea_bolivia"
          data-embed-from="embed_page"
          data-embed-type="creator"
          style={{ maxWidth: 780, minWidth: 288 }}
        >
          <section>
            <a
              target="_blank"
              href="https://www.tiktok.com/@nea_bolivia?refer=creator_embed"
              rel="noreferrer"
            >
              @nea_bolivia
            </a>
          </section>
        </blockquote>
      )}
      {showOfferSections && productsInOffer.length > 0 && (
        <>
          <h2 style={{ textAlign: 'center', margin: '20px 0' }}>Ofertas</h2>

          <Suspense fallback={<div className="home-carousel-skeleton" />}>
            <AliceCarousel {...carouselOptions} className="alice-carousel">
              {filteredProductsInOffer.map((product) => {
                const { bestOffer, lowestPrice } = calculateBestOffer(product);
                const hasOffer =
                  product.data.offers &&
                  Object.keys(product.data.offers).length > 0;
                let showOfferBadge = hasOffer;

                if (hasOffer) {
                  showOfferBadge = Object.values(product.data.offers).some(
                    (offer) => {
                      return offer.applicability !== 'vip' || isVip(currentUser);
                    },
                  );
                }

                return (
                  <div
                    key={`offer-${product.id}`}
                    className="mb-item"
                    onClick={() => onClickProduct(product.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="mb-item-image">
                      <Carousel className="previews-slider" shape="bar" autoplay>
                        {product.data.images.map((imgId) => (
                          <img
                            key={imgId}
                            src={getProductThumbnailUrl(imgId)}
                            alt={product.data.name}
                            className="mb-item-image-element"
                            loading="lazy"
                            decoding="async"
                          />
                        ))}
                      </Carousel>
                      {showOfferBadge && (
                        <div className="offer-badge">Oferta</div>
                      )}
                      <button
                        className={`heart-button ${products.favorites.includes(product.id) ? 'filled' : ''}`}
                        onClick={(e) => toggleFavoriteClick(product, e)}
                      >
                        <i className="fas fa-heart"></i>
                      </button>
                    </div>
                    <div className="mb-item-title">{product.data.name}</div>
                    <div className="mb-item-prices">
                      {renderOfferPrices(product, bestOffer, lowestPrice)}
                    </div>

                    {(!product.data.variables ||
                      product.data.variables?.length <= 0) && (
                      <div className="mb-item-button">
                        {isProductInCart(product.id) ? (
                          <span>
                            {' '}
                            <i className="fas fa-shopping-cart"></i> En el carrito
                          </span>
                        ) : (
                          <Button
                            onClick={(e) => addProductClick(product, null, e)}
                          >
                            <i className="fas fa-shopping-cart"></i> Añadir al
                            carrito
                          </Button>
                        )}
                      </div>
                    )}
                    {product.data.variables?.length > 0 && (
                      <div className="mb-item-button">
                        {isProductInCart(product.id) ? (
                          <span>
                            {' '}
                            <i className="fas fa-shopping-cart"></i> En el carrito
                          </span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'row' }}>
                            {product.data.variables?.map((variant, index) => (
                              <div
                                key={`var-${index}`}
                                className="mb-item-button"
                              >
                                <Button
                                  onClick={(e) =>
                                    addProductClick(product, variant, e)
                                  }
                                >
                                  <i className="fas fa-shopping-cart"></i>{' '}
                                  {variant.name}
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </AliceCarousel>
          </Suspense>
        </>
      )}

      <Modal open={selectedProduct !== null} onClose={closeProductModal}>
        <Modal.Header>
          <Modal.Title>{selectedProduct?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p
            dangerouslySetInnerHTML={{ __html: selectedProduct?.description }}
          />
        </Modal.Body>
        <Modal.Footer></Modal.Footer>
      </Modal>
      <Footer />
    </div>
  );
}

export default Home;
